/* =========================================================
   FILE: page-themendecks.js
   Themenfilter, Suche, Filter-Fades, Titel-Overflow und FAQ.
========================================================= */

(() => {
  'use strict';

  const initialized = new WeakSet();

  const initialize = (section) => {
    if (!section || initialized.has(section)) return;
    initialized.add(section);

    const filters = Array.from(section.querySelectorAll('[data-cn-td-filter]'));
    const filtersShell = section.querySelector('[data-cn-td-filters-shell]');
    const filtersScroller = section.querySelector('[data-cn-td-filters]');
    const searchInput = section.querySelector('[data-cn-td-search]');
    const searchWrap = section.querySelector('.cn-themendecks__search');
    const searchClear = section.querySelector('[data-cn-td-search-clear]');
    const items = Array.from(section.querySelectorAll('[data-cn-td-type]'));
    const groups = Array.from(section.querySelectorAll('.cn-themendecks__letter-group'));
    const columns = Array.from(section.querySelectorAll('.cn-themendecks__column'));
    const noResults = section.querySelector('[data-cn-td-no-results]');
    const nameShells = Array.from(section.querySelectorAll('[data-cn-td-name-shell]'));

    if (!filters.length || !items.length) return;

    const getInitialFilterFromHash = () => {
      const hash = window.location.hash.replace('#', '').trim().toLowerCase();
      const allowedFilters = ['themendeck', 'serie', 'charakter', 'kollektion'];

      return allowedFilters.includes(hash) ? hash : 'all';
    };

    let currentFilter = getInitialFilterFromHash();
    let currentSearch = searchInput ? searchInput.value : '';
    let filterShadowRaf = null;
    let nameShadowRaf = null;

    const normalizeText = (value) => {
      return String(value || '')
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    };

    const itemData = items.map((item) => {
      const itemType = item.dataset.cnTdType || '';
      const itemName = item.querySelector('.cn-themendecks__name')?.textContent || '';
      const itemTypeLabel = item.querySelector('.cn-themendecks__type')?.textContent || '';

      return {
        element: item,
        type: itemType,
        searchableText: normalizeText(`${itemName} ${itemTypeLabel}`)
      };
    });

    const groupData = groups.map((group) => {
      return {
        element: group,
        items: Array.from(group.querySelectorAll('.cn-themendecks__item'))
      };
    });

    const getMatchesSearch = (item, normalizedSearch) => {
      return normalizedSearch === '' || item.searchableText.includes(normalizedSearch);
    };

    const updateNameShadows = () => {
      nameShells.forEach((shell) => {
        const scroller = shell.querySelector('[data-cn-td-name]');
        if (!scroller) return;

        const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        const scrollLeft = Math.max(0, scroller.scrollLeft);
        const hasOverflow = maxScrollLeft > 1;

        shell.classList.toggle('has-overflow', hasOverflow);
        shell.classList.toggle('is-at-start', !hasOverflow || scrollLeft <= 1);
        shell.classList.toggle('is-at-end', !hasOverflow || scrollLeft >= maxScrollLeft - 1);
      });
    };

    const scheduleNameShadows = () => {
      if (nameShadowRaf) {
        window.cancelAnimationFrame(nameShadowRaf);
      }

      nameShadowRaf = window.requestAnimationFrame(() => {
        nameShadowRaf = null;
        updateNameShadows();
      });
    };

    const updateFilterShadow = () => {
      if (!filtersShell || !filtersScroller) return;

      const maxScrollLeft = Math.max(0, filtersScroller.scrollWidth - filtersScroller.clientWidth);
      const scrollLeft = filtersScroller.scrollLeft;
      const hasOverflow = maxScrollLeft > 1;
      const isAtStart = scrollLeft <= 1;
      const isAtEnd = scrollLeft >= maxScrollLeft - 1;

      filtersShell.classList.toggle('has-overflow', hasOverflow);
      filtersShell.classList.toggle('is-at-start', !hasOverflow || isAtStart);
      filtersShell.classList.toggle('is-at-end', !hasOverflow || isAtEnd);
    };

    const scheduleFilterShadow = () => {
      if (filterShadowRaf) {
        window.cancelAnimationFrame(filterShadowRaf);
      }

      filterShadowRaf = window.requestAnimationFrame(() => {
        filterShadowRaf = null;
        updateFilterShadow();
      });
    };

    const updateSearchState = () => {
      if (!searchInput || !searchWrap || !searchClear) return;

      const hasValue = searchInput.value.trim() !== '';

      searchWrap.classList.toggle('has-value', hasValue);
      searchClear.disabled = !hasValue;
    };

    const updateFilterCounts = () => {
      const normalizedSearch = normalizeText(currentSearch);
      const counts = {
        all: 0,
        themendeck: 0,
        serie: 0,
        charakter: 0,
        kollektion: 0
      };

      itemData.forEach((item) => {
        if (!getMatchesSearch(item, normalizedSearch)) return;

        counts.all += 1;

        if (Object.prototype.hasOwnProperty.call(counts, item.type)) {
          counts[item.type] += 1;
        }
      });

      filters.forEach((button) => {
        const filterType = button.dataset.cnTdFilter;
        const countElement = button.querySelector('.cn-themendecks__filter-count');

        if (countElement) {
          countElement.textContent = counts[filterType] ?? 0;
        }
      });
    };

    const updateGroupsAndColumns = () => {
      let visibleGroups = 0;

      groupData.forEach((group) => {
        const hasVisibleItems = group.items.some((item) => !item.hidden);

        group.element.hidden = !hasVisibleItems;

        if (hasVisibleItems) {
          visibleGroups += 1;
        }
      });

      columns.forEach((column) => {
        const hasVisibleGroups = Array.from(column.querySelectorAll('.cn-themendecks__letter-group'))
          .some((group) => !group.hidden);

        column.hidden = !hasVisibleGroups;
      });

      if (noResults) {
        noResults.hidden = visibleGroups > 0;
      }
    };

    const updateItems = () => {
      const normalizedSearch = normalizeText(currentSearch);

      itemData.forEach((item) => {
        const matchesFilter = currentFilter === 'all' || item.type === currentFilter;
        const matchesSearch = getMatchesSearch(item, normalizedSearch);

        item.element.hidden = !(matchesFilter && matchesSearch);
      });

      updateGroupsAndColumns();
      updateFilterCounts();
      scheduleFilterShadow();
      scheduleNameShadows();
    };

    const setActiveFilterButton = () => {
      filters.forEach((button) => {
        const isActive = button.dataset.cnTdFilter === currentFilter;

        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    };

    filters.forEach((button) => {
      button.addEventListener('click', () => {
        currentFilter = button.dataset.cnTdFilter || 'all';

        setActiveFilterButton();

        if (currentFilter === 'all') {
          history.replaceState(null, '', window.location.pathname);
        } else {
          history.replaceState(null, '', `${window.location.pathname}#${currentFilter}`);
        }

        updateItems();
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        currentSearch = searchInput.value;

        updateSearchState();
        updateItems();
      });
    }

    if (searchClear && searchInput) {
      searchClear.addEventListener('click', () => {
        if (searchClear.disabled) return;

        searchInput.value = '';
        currentSearch = '';

        updateSearchState();
        updateItems();
        searchInput.focus();
      });
    }

    if (filtersScroller) {
      filtersScroller.addEventListener('scroll', scheduleFilterShadow, { passive: true });
    }

    nameShells.forEach((shell) => {
      const scroller = shell.querySelector('[data-cn-td-name]');
      if (!scroller) return;

      scroller.addEventListener('scroll', scheduleNameShadows, { passive: true });

      let pointerId = null;
      let startX = 0;
      let startScrollLeft = 0;
      let didDrag = false;

      const stopDrag = () => {
        if (pointerId === null) return;

        if (scroller.hasPointerCapture?.(pointerId)) {
          scroller.releasePointerCapture(pointerId);
        }

        pointerId = null;
        scroller.classList.remove('is-dragging');
        scheduleNameShadows();
      };

      scroller.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (scroller.scrollWidth <= scroller.clientWidth + 1) return;

        pointerId = event.pointerId;
        startX = event.clientX;
        startScrollLeft = scroller.scrollLeft;
        didDrag = false;

        scroller.setPointerCapture?.(pointerId);
        scroller.classList.add('is-dragging');
      });

      scroller.addEventListener('pointermove', (event) => {
        if (pointerId !== event.pointerId) return;

        const deltaX = event.clientX - startX;

        if (!didDrag && Math.abs(deltaX) > 4) {
          didDrag = true;
        }

        if (!didDrag) return;

        event.preventDefault();
        scroller.scrollLeft = startScrollLeft - deltaX;
        scheduleNameShadows();
      });

      scroller.addEventListener('pointerup', stopDrag);
      scroller.addEventListener('pointercancel', stopDrag);
      scroller.addEventListener('lostpointercapture', stopDrag);

      scroller.addEventListener(
        'click',
        (event) => {
          if (!didDrag) return;

          event.preventDefault();
          event.stopPropagation();
          didDrag = false;
        },
        true
      );
    });

    window.addEventListener('resize', () => {
      scheduleFilterShadow();
      scheduleNameShadows();
    }, { passive: true });

    if ('ResizeObserver' in window) {
      if (filtersScroller) {
        const filterResizeObserver = new ResizeObserver(scheduleFilterShadow);
        filterResizeObserver.observe(filtersScroller);
      }

      nameShells.forEach((shell) => {
        const scroller = shell.querySelector('[data-cn-td-name]');
        if (!scroller) return;

        const nameResizeObserver = new ResizeObserver(scheduleNameShadows);
        nameResizeObserver.observe(scroller);
      });
    }

    setActiveFilterButton();
    updateSearchState();
    updateItems();
    scheduleFilterShadow();
    scheduleNameShadows();

    const faqItems = section.querySelectorAll('.cn-themendecks__faq-item');
    if (!faqItems.length) return;

    faqItems.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;

        faqItems.forEach((otherItem) => {
          if (otherItem !== item) {
            otherItem.removeAttribute('open');
          }
        });
      });
    });
  };

  const initializeAll = (scope = document) => {
    if (scope.matches?.('.cn-themendecks')) initialize(scope);
    scope.querySelectorAll?.('.cn-themendecks').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();